/**
 * Leitura do 422 `CADASTRO_INCOMPLETO` dos endpoints de escrita de paciente.
 *
 * FORMATO (contrato, conferido em `GlobalExceptionHandler.esusCadastroIncompletoException`): o corpo
 * do axios chega em `error.response.data.data` como
 * `{message, bloqueio: 'CADASTRO_INCOMPLETO', erros: [{campo, correcao}]}`.
 *
 * NADA DAQUI PASSA POR `showError`/`buildErrorMessage`: os dois injetam HTML não sanitizado no Swal
 * (`AlertHelper.ts:73-82` + `BuildErrorMessage.ts:12`, achado F6 da fatia F7c). As mensagens saem
 * como **texto** — em `formik.setFieldError` e no resumo renderizado pelo React, que escapa por
 * construção.
 *
 * O de/para campo canônico → campo do formulário é o de `_fciCampos.ts`, o mesmo que o schema
 * dinâmico usa: se ele fosse escrito duas vezes, a tela começaria a divergir de si mesma.
 */

import { ContextoFormularioFci, resolverCampoFormik, rotuloCampoFci } from './_fciCampos'

export const BLOQUEIO_CADASTRO_INCOMPLETO = 'CADASTRO_INCOMPLETO'

export type ErroCampoFci = {
  campo: string
  correcao: string
}

export type Cadastro422 = {
  message?: string
  bloqueio?: string
  erros?: ErroCampoFci[]
}

/** Um erro já traduzido para a tela. */
export type ErroTraduzidoFci = {
  /** Campo do formik, ou `null` quando o campo canônico não tem destino conhecido. */
  campoFormik: string | null
  /** Nome canônico original, preservado para o resumo e para depuração. */
  campoCanonico: string
  /** Rótulo pt-BR; cai no próprio nome canônico quando o campo é desconhecido. */
  rotulo: string
  /** Texto do backend, exibido como texto literal. */
  correcao: string
}

/**
 * Extrai o corpo do 422 de um erro do axios. Devolve `null` para qualquer outra falha (rede, 500,
 * 403), que segue o tratamento genérico de sempre.
 */
export const lerCadastroIncompleto = (error: any): Cadastro422 | null => {
  const corpo = error?.response?.data?.data
  if (!corpo || corpo.bloqueio !== BLOQUEIO_CADASTRO_INCOMPLETO) return null
  return corpo as Cadastro422
}

/**
 * Traduz os erros do 422 para campos do formulário.
 *
 * Campo canônico sem destino **não some**: volta com `campoFormik: null` para quem chama exibir no
 * resumo do topo com a `correcao` original — esconder seria repetir o 422 opaco que esta fatia
 * existe para acabar.
 */
export const traduzirErrosFci = (
  erros: ErroCampoFci[] | undefined,
  contexto: ContextoFormularioFci = {}
): ErroTraduzidoFci[] =>
  (erros || []).map((erro) => ({
    campoFormik: resolverCampoFormik(erro.campo, contexto),
    campoCanonico: erro.campo,
    rotulo: rotuloCampoFci(erro.campo) ?? erro.campo,
    correcao: erro.correcao,
  }))
